//rfq view
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ORDER_API, MASTER_API } from "../../../apiConfig";
import sbeeLogo from "../../../assets/sbeelogo.png";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useSearchParams } from "react-router-dom";

// Seal/Sign files are served as static files off the master-service root
// (e.g. http://localhost:5001/uploads/seal-sign/xyz.jpeg), NOT under the
// "/api" prefix that MASTER_API points to — so strip a trailing "/api"
// (or "/api/...") from MASTER_API to get that root. Same pattern used in
// ProductMaster.js for the Seal & Sign config screen's "View File" link.
const SEAL_SIGN_FILE_BASE = MASTER_API.replace(/\/api(\/.*)?$/, "");

// ── Fallback text for the General Terms & Conditions table — used ONLY if
//    the master-service template (fetched below into `templateTerms`) can't
//    be reached, so the quotation still renders something sensible. The real
//    source of truth is now Configuration > Terms & Conditions (master
//    service), edited from TermsConditionsConfig.js. term_no 6 (Payment
//    Terms) and 10 (Validity of Offer) are computed dynamically from
//    rfqData rather than a fixed default — see getDefaultTermText(). ──
const FALLBACK_TERMS_DEFAULTS = [
  { term_no: 1, title: "SCOPE OF SUPPLY", text: "Our Scope of supply is limited to manufacturing, testing at our works, loading at our works, supply, transit insurance, packing and forwarding of quoted cables. Any installation, testing, commission are not in our scope of supply." },
  { term_no: 2, title: "PRICES", text: "The quoted Prices are FIRM basis and FOR Site basis. Kindly note freight charges we have considered in full load truck basis, in case of part load dispatch in cargo, if any additional freight charges is required, same shall be charged extra." },
  { term_no: 3, title: "FREIGHT", text: "Inclusive. GST @ 18% is also applicable on freight charges. Unloading and its charges shall be arranged and paid by buyer only." },
  { term_no: 4, title: "GST", text: "Integrated Goods & Service Tax (IGST): Extra at actual subject to statutory variation clause. Present rate is 18%." },
  { term_no: 5, title: "DELIVERY", text: "Ex-Works delivery shall be completed within 4 to 6 weeks from the date of receipt of technically and commercially clear purchase order or manufacturing clearance or advance payment whichever is received later. Ex-Works means the date of intimation of readiness of material to you or to your nominated inspection agency. However, we may discuss mutually at the time of order finalization." },
  { term_no: 6, title: "PAYMENT TERMS", text: null },
  { term_no: 7, title: "TYPE TEST, INSPECTION AND ITS CHARGES", text: "Any Type Test and Inspection if required, same shall be arranged and paid by buyer only." },
  { term_no: 8, title: "PACKING LENGTH & QUANTITY TOLERANCE", text: "The material shall be supplied in Wooden Drums. The nominal length shall be supplied in drum length of 1000 Mtr with +/-5% tolerance. Tolerance on total ordered quantity shall be +/-5%." },
  { term_no: 9, title: "WARRANTY PERIOD", text: "18 (Eighteen) months from the date of dispatch or 12 (twelve) months from the date of commissioning whichever is earlier subject to the conditions mentioned at sl.no.2 of our General Terms and Conditions attached with this offer. During Warranty Period if any defects are attributable directly due to defective material or poor workmanship, our liability in this respect shall be limited to free replacement/repair of the defective portion of the cables only. Sufficiently reasonable time and facilities at site will be provided by buyer enabling us to establish the cause/source of defect. Any consequential damages are not in our scope of supply." },
  { term_no: 10, title: "VALIDITY OF OFFER", text: null },
];

function QuotationView() {

  const { rfqId } = useParams();

  const [rfqData, setRfqData] = useState(null);
  // "created_by" is sometimes a real email ("riya.r@digizura.com") and
  // sometimes just a username ("Nikshith") depending on how the RFQ was
  // created. When it's just a username, we resolve the real email here so
  // the "Email Id" row never shows a username by mistake.
  const [createdByEmail, setCreatedByEmail] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [companyLocation, setCompanyLocation] = useState(null); // from Configuration > Location Master (Unit 2 / manufacturing — also used in the top letterhead)
  const [unit1Location, setUnit1Location] = useState(null); // from Configuration > Location Master (Unit 1 / Regd. Office)
  // Shared display name for the company — used at BOTH the top letterhead
  // and the footer, so a single Location Master edit updates both. Prefers
  // the Unit 2 (manufacturing) record, falls back to Unit 1 (Regd. Office),
  // then to the static name if neither has been configured yet.
  const companyDisplayName =
    unit1Location?.company_name || companyLocation?.company_name || "SBEE CABLES (INDIA) LIMITED";
  const [savedTerms, setSavedTerms] = useState([]); // [{term_no, term_text}] overrides from rfq_terms_conditions
  // ── Terms & Conditions TEMPLATE — fetched from master service
  //    (Configuration > Terms & Conditions). This is the shared source used
  //    by every quotation unless a specific quotation has its own override
  //    in `savedTerms`. Starts out as the fallback so the page still renders
  //    while the fetch is in flight / if it fails. ──
  const [templateTerms, setTemplateTerms] = useState(FALLBACK_TERMS_DEFAULTS);
  // Seal & Sign records from Configuration > Seal & Sign (master-service),
  // e.g. [{ id, username, file_path }, ...]. Used below to print the
  // approver's actual signature/seal image under "Approved by".
  const [sealSignList, setSealSignList] = useState([]);
  const [usersList, setUsersList] = useState([]); // usererp rows, to resolve a stored full name back to a user id
  const [searchParams] = useSearchParams();
  const getAuthHeaders = () => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("jwt");

  return token
    ? { Authorization: `Bearer ${token}` }
    : {};
};

  // ── General Terms & Conditions — inline edit state ──
  const [editingTerms, setEditingTerms] = useState(false);
  const [termsDraft, setTermsDraft] = useState({}); // { [term_no]: term_text }
  const [savingTerms, setSavingTerms] = useState(false);

  // ── Letterhead (company name/address block) — inline edit state.
  //    Saved as a per-RFQ override in rfq_letterhead_override, so editing it
  //    changes THIS quotation only and never touches Configuration >
  //    Location Master or any other quotation. ──
  const [letterheadOverride, setLetterheadOverride] = useState(null);
  const [editingLetterhead, setEditingLetterhead] = useState(false);
  const [letterheadDraft, setLetterheadDraft] = useState({});
  const [savingLetterhead, setSavingLetterhead] = useState(false);

  // ── "Address and Contact details" (customer block) — inline edit state.
  //    Saved as a per-RFQ override in rfq_customer_override, so editing it
  //    changes THIS quotation only and never touches the Business Partner
  //    master or any other quotation. Mirrors the letterhead pattern above. ──
  const [customerOverride, setCustomerOverride] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [customerDraft, setCustomerDraft] = useState({});
  const [savingCustomer, setSavingCustomer] = useState(false);

  // ── Greeting line ("We thank you for your valuable enquiry...") — inline
  //    edit state. Saved as a per-RFQ override in rfq_greeting_override, so
  //    editing it changes THIS quotation only. Mirrors the letterhead/
  //    customer-block pattern above. ──
  const DEFAULT_GREETING_TEXT =
    "We thank you for your valuable enquiry. Please find below our best offer for the same.";
  const [greetingOverride, setGreetingOverride] = useState(null);
  const [editingGreeting, setEditingGreeting] = useState(false);
  const [greetingDraft, setGreetingDraft] = useState("");
  const [savingGreeting, setSavingGreeting] = useState(false);
  const effectiveGreeting = greetingOverride ?? DEFAULT_GREETING_TEXT;

  const STATIC_LETTERHEAD_DEFAULTS = {
    company_name: "SBEE CABLES (INDIA) LIMITED",
    address_line1: "No.282 & 283",
    address_line2: "KIADB Industrial Area, Phase 2, Harohalli, Kanakapura Taluk,",
    city: "Ramanagar",
    pin_code: "562112",
    state: "Karnataka",
    country: "India",
    tel_no: "080-35742421",
    gst_no: "29AABCM5279M1ZT",
    cin_no: "U64203KA1978PLC003385",
  };

  // Location Master (Unit 1) values, falling back to the static defaults
  // field-by-field so a partially-filled Location Master record still works.
  const baseLetterhead = unit1Location
    ? {
        company_name: unit1Location.company_name || STATIC_LETTERHEAD_DEFAULTS.company_name,
        address_line1: unit1Location.address_line1 || STATIC_LETTERHEAD_DEFAULTS.address_line1,
        address_line2: unit1Location.address_line2 || STATIC_LETTERHEAD_DEFAULTS.address_line2,
        city: unit1Location.city || STATIC_LETTERHEAD_DEFAULTS.city,
        pin_code: unit1Location.pin_code || STATIC_LETTERHEAD_DEFAULTS.pin_code,
        state: unit1Location.state || STATIC_LETTERHEAD_DEFAULTS.state,
        country: unit1Location.country || STATIC_LETTERHEAD_DEFAULTS.country,
        tel_no: unit1Location.tel_no || STATIC_LETTERHEAD_DEFAULTS.tel_no,
        gst_no: unit1Location.gst_no || STATIC_LETTERHEAD_DEFAULTS.gst_no,
        cin_no: unit1Location.cin_no || STATIC_LETTERHEAD_DEFAULTS.cin_no,
      }
    : STATIC_LETTERHEAD_DEFAULTS;

  // Effective values actually shown: per-quotation override wins field-by-field
  // over Location Master / static defaults.
  const effectiveLetterhead = { ...baseLetterhead, ...(letterheadOverride || {}) };

  const handleEditLetterhead = () => {
    setLetterheadDraft({ ...effectiveLetterhead });
    setEditingLetterhead(true);
  };

  const handleCancelLetterhead = () => {
    setEditingLetterhead(false);
    setLetterheadDraft({});
  };

  const handleSaveLetterhead = async () => {
    try {
      setSavingLetterhead(true);
      const res = await fetch(`${ORDER_API}/quotation/${rfqId}/letterhead`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ letterhead: letterheadDraft }),
      });

      if (!res.ok) throw new Error("Failed to save letterhead");

      setLetterheadOverride({ ...letterheadDraft });
      setEditingLetterhead(false);
    } catch (err) {
      console.error("saveLetterhead Error:", err);
      alert("Failed to save the company name/address. Please try again.");
    } finally {
      setSavingLetterhead(false);
    }
  };

  // ── FIX: Safe single-value parser — handles plain string OR JSON array
  //         (e.g. contact_details / contact_person_name / email are all
  //         sometimes saved as JSON array strings like '["maithili"]') ──
  const parseSingleValue = (val) => {
    if (!val) return "-";
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed[0] || "-";
      return String(parsed);
    } catch {
      return String(val); // already a plain string
    }
  };

  const parseEmail = parseSingleValue;

  // Base values for the customer block, straight from the Business Partner
  // record (via customerData), field-by-field — same shape/order as the
  // effective/override fields below so the Edit form can draft over them.
  // Picks the first non-empty value, so if the partner was saved with a
  // Billing or Supplier address instead of Shipping, that is used instead.
  const pick = (...vals) => {
    const v = vals.find((x) => x !== null && x !== undefined && String(x).trim() !== "");
    return v === undefined ? "-" : v;
  };
  const cd = customerData || {};
  const baseCustomer = {
    business_partner_name: pick(cd.business_partner_name, cd.legal_name),
    shipping_line1: pick(cd.shipping_line1, cd.billing_line1, cd.address_line1),
    shipping_line2: pick(cd.shipping_line2, cd.billing_line2, cd.address_line2),
    shipping_city: pick(cd.shipping_city, cd.billing_city, cd.address_city),
    shipping_state: pick(cd.shipping_state, cd.billing_state, cd.address_state),
    shipping_zip: pick(cd.shipping_zip, cd.billing_zip, cd.address_zip),
    contact_person_name: parseSingleValue(cd.contact_person_name),
    contact_details: parseSingleValue(cd.contact_details),
    email: parseEmail(cd.email),
    gstin: pick(cd.gstin),
  };


  // Effective values actually shown: per-quotation override wins field-by-field
  // over the Business Partner master.
  // Ignore override values that are empty or just the "-" placeholder, so a
  // previously saved blank override can't hide real Business Partner data.
  const meaningfulOverride = Object.fromEntries(
    Object.entries(customerOverride || {}).filter(([, v]) => {
      const t = String(v ?? "").trim();
      return t !== "" && t !== "-";
    })
  );
  const effectiveCustomer = { ...baseCustomer, ...meaningfulOverride };

  const handleEditCustomer = () => {
    setCustomerDraft({ ...effectiveCustomer });
    setEditingCustomer(true);
  };

  const handleCancelCustomer = () => {
    setEditingCustomer(false);
    setCustomerDraft({});
  };

  const handleSaveCustomer = async () => {
    try {
      setSavingCustomer(true);
      const res = await fetch(`${ORDER_API}/quotation/${rfqId}/customer-address`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ customer: customerDraft }),
      });

      if (!res.ok) throw new Error("Failed to save customer details");

      setCustomerOverride({ ...customerDraft });
      setEditingCustomer(false);
    } catch (err) {
      console.error("saveCustomer Error:", err);
      alert("Failed to save the address and contact details. Please try again.");
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleEditGreeting = () => {
    setGreetingDraft(effectiveGreeting);
    setEditingGreeting(true);
  };

  const handleCancelGreeting = () => {
    setEditingGreeting(false);
    setGreetingDraft("");
  };

  const handleSaveGreeting = async () => {
    try {
      setSavingGreeting(true);
      const res = await fetch(`${ORDER_API}/quotation/${rfqId}/greeting`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ greeting_text: greetingDraft }),
      });

      if (!res.ok) throw new Error("Failed to save greeting line");

      setGreetingOverride(greetingDraft);
      setEditingGreeting(false);
    } catch (err) {
      console.error("saveGreeting Error:", err);
      alert("Failed to save the greeting line. Please try again.");
    } finally {
      setSavingGreeting(false);
    }
  };

  // ── Single master Edit/Save/Cancel control (shown once, above everything)
  //    that drives all three per-quotation overrides above together, so the
  //    user doesn't have to click three separate Edit buttons. ──
  const [editingAll, setEditingAll] = useState(false);
  const savingAll = savingLetterhead || savingCustomer || savingGreeting;

  const handleEditAll = () => {
    handleEditLetterhead();
    handleEditCustomer();
    handleEditGreeting();
    setEditingAll(true);
  };

  const handleCancelAll = () => {
    handleCancelLetterhead();
    handleCancelCustomer();
    handleCancelGreeting();
    setEditingAll(false);
  };

  const handleSaveAll = async () => {
    await Promise.all([handleSaveLetterhead(), handleSaveCustomer(), handleSaveGreeting()]);
    setEditingAll(false);
  };
  const nameFromEmail = (val) => {
    if (!val) return "";
    const namePart = String(val).split("@")[0];
    if (!namePart) return "";
    return namePart.charAt(0).toUpperCase() + namePart.slice(1);
  };

  // Resolves the real email for "created_by" whenever it's stored as a
  // bare username instead of an email address.
  //
  // Uses the same real users endpoint the Seal & Sign config dropdown uses
  // (GET /configuration/seal-sign/users → SELECT * FROM usererp), since
  // there's no dedicated "/user-master" endpoint on the backend — that was
  // a placeholder guess and always 404'd, which is why Email Id used to
  // always show "-". We fetch the full user list once and match by
  // username, trying several common column names for the email field since
  // we can't see the usererp schema directly.
  useEffect(() => {
    const createdBy = rfqData?.created_by;
    if (!createdBy) {
      setCreatedByEmail(null);
      return;
    }
    if (createdBy.includes("@")) {
      // Already a real email — use it as-is.
      setCreatedByEmail(createdBy);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${MASTER_API}/configuration/seal-sign/users`,
          { headers: { ...getAuthHeaders() } }
        );
        if (!res.ok) throw new Error("lookup failed");
        const users = await res.json();
        const createdByKey = String(createdBy).trim().toLowerCase();
        const match = Array.isArray(users)
          ? users.find((u) => {
              const fullName = `${u.first_name || ""} ${u.last_name || ""}`.trim();
              const candidates = [u.user_id, u.username, u.name, fullName].filter(Boolean);
              return candidates.some((c) => String(c).trim().toLowerCase() === createdByKey);
            })
          : null;
        // Try the common column-name spellings for email since the exact
        // usererp schema isn't visible from the frontend.
        const email =
          match?.email || match?.user_email || match?.Email || match?.emailId || match?.mail || null;
        if (!cancelled) setCreatedByEmail(email);
      } catch (err) {
        console.warn("Could not resolve email for created_by:", createdBy, err);
        if (!cancelled) setCreatedByEmail(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rfqData?.created_by]);

  // Seal & Sign list — fetched once on mount so we can print the approver's
  // signature/seal image under "Approved by" further down.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${MASTER_API}/configuration/seal-sign`);
        if (!res.ok) throw new Error("seal-sign fetch failed");
        const data = await res.json();
        if (!cancelled) setSealSignList(Array.isArray(data) ? data : []);
      } catch (err) {
        console.warn("Could not fetch seal/sign list:", err);
        if (!cancelled) setSealSignList([]);
      }
      try {
        const uRes = await fetch(`${MASTER_API}/configuration/seal-sign/users`, {
          headers: { ...getAuthHeaders() },
        });
        if (!uRes.ok) throw new Error("users fetch failed");
        const uData = await uRes.json();
        if (!cancelled) setUsersList(Array.isArray(uData) ? uData : []);
      } catch (err) {
        console.warn("Could not fetch users list:", err);
        if (!cancelled) setUsersList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Matches the approver (rfqData.internal_action_by, which may be a bare
  // username OR a full email like "maithili@company.com") against the
  // Seal & Sign list's `username` column, case-insensitively and ignoring
  // the email domain, so it works with however the RFQ stores the value.
  const approverSealUrl = (() => {
    const approver = rfqData?.internal_action_by;
    if (!approver || !sealSignList.length) return null;
    const approverKey = String(approver).split("@")[0].trim().toLowerCase();
    // approved_by is now stored as the person's full name ("Maithili M"),
    // but Seal & Sign records are keyed by user id — so resolve the name
    // to that user's id/username via the users list as well.
    const keys = new Set([approverKey]);
    usersList.forEach((u) => {
      const fullName = `${u.first_name || ""} ${u.last_name || ""}`.trim();
      const candidates = [u.user_id, u.username, u.name, fullName]
        .filter(Boolean)
        .map((c) => String(c).split("@")[0].trim().toLowerCase());
      if (candidates.includes(approverKey)) {
        [u.user_id, u.username].filter(Boolean).forEach((k) => keys.add(String(k).trim().toLowerCase()));
      }
    });
    const match = sealSignList.find((s) =>
      keys.has(String(s.username || "").trim().toLowerCase())
    );
    return match?.file_path ? `${SEAL_SIGN_FILE_BASE}${match.file_path}` : null;
  })();

  // ── Once the RFQ has entered/passed the internal approval stage, the
  //    customer-facing quotation should list ONLY line items individually
  //    marked "Approved" — Rejected / Query / still-Pending rows must be
  //    left out of the document. Before that stage (Draft / Pending for
  //    Approval) internal_action is still null on every row, so we fall
  //    back to showing everything so the quotation isn't blank. ──
  const POST_APPROVAL_STATUSES = [
    "Approved", "Rejected", "Partially Approved", "Partially Rejected",
    "Submitted to Customer", "Approved by Customer", "Rejected by Customer",
    "Partially Approved by Customer", "Partially Rejected by Customer",
  ];
  const isPostApprovalStage = POST_APPROVAL_STATUSES.includes(rfqData?.rfq_status);

  // ── Once the customer has actually reviewed the quotation (Approved by
  //    Customer / Rejected by Customer / Partially Approved by Customer /
  //    Partially Rejected by Customer), each line item also carries its own
  //    customer_approval_status. From that point on a row must be approved
  //    on BOTH sides — internally AND by the customer — to still appear;
  //    a row the customer rejected must drop out even though it was
  //    internally approved earlier. ──
  const CUSTOMER_REVIEWED_STATUSES = [
    "Approved by Customer", "Rejected by Customer",
    "Partially Approved by Customer", "Partially Rejected by Customer",
  ];
  const isCustomerReviewedStage = CUSTOMER_REVIEWED_STATUSES.includes(rfqData?.rfq_status);

  const displayedProductDetails = isCustomerReviewedStage
    ? (rfqData?.productDetails || []).filter(
        (item) =>
          item.internal_action === "Approved" &&
          (item.customer_approval_status || "").trim().toLowerCase() === "approved"
      )
    : isPostApprovalStage
    ? (rfqData?.productDetails || []).filter((item) => item.internal_action === "Approved")
    : (rfqData?.productDetails || []);

  // ── General Terms & Conditions — inline edit helpers ──
  const getDefaultTermText = (term_no) => {
    if (term_no === 6) return rfqData?.payment_terms || "-";
    if (term_no === 10) {
      return rfqData?.validity_date
        ? `This offer is valid up to ${new Date(rfqData.validity_date).toLocaleDateString("en-GB")} for acceptance of PO/LOI.`
        : "-";
    }
    return templateTerms.find((t) => t.term_no === term_no)?.text || "";
  };

  const getEffectiveTermText = (term_no) => {
    const override = (savedTerms || []).find((t) => Number(t.term_no) === term_no);
    return override ? override.term_text : getDefaultTermText(term_no);
  };

  const handleEditTerms = () => {
    const draft = {};
    templateTerms.forEach((t) => {
      draft[t.term_no] = getEffectiveTermText(t.term_no);
    });
    setTermsDraft(draft);
    setEditingTerms(true);
  };

  const handleCancelTerms = () => {
    setEditingTerms(false);
    setTermsDraft({});
  };

  const handleSaveTerms = async () => {
    try {
      setSavingTerms(true);
      const terms = templateTerms.map((t) => ({
        term_no: t.term_no,
        term_text: (termsDraft[t.term_no] ?? "").toString(),
      }));

      const res = await fetch(`${ORDER_API}/quotation/${rfqId}/terms`, {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    ...getAuthHeaders(),
  },
  body: JSON.stringify({ terms }),
});

      if (!res.ok) throw new Error("Failed to save terms");

      setSavedTerms(terms);
      setEditingTerms(false);
    } catch (err) {
      console.error("saveTerms Error:", err);
      alert("Failed to save Terms & Conditions. Please try again.");
    } finally {
      setSavingTerms(false);
    }
  };

  const downloadPDF = async () => {
    let clone = null;
    try {
      const input = document.getElementById("quotation-container");

      // ── 1. Pre-convert logo to base64 BEFORE cloning ──
      //    This avoids CORS/taint issues with bundled asset URLs entirely.
      let logoBase64 = null;
      await new Promise((resolve) => {
        const tempImg = new Image();
        tempImg.onload = () => {
          try {
            const cvs = document.createElement("canvas");
            cvs.width  = tempImg.naturalWidth  || 440;
            cvs.height = tempImg.naturalHeight || 200;
            cvs.getContext("2d").drawImage(tempImg, 0, 0);
            logoBase64 = cvs.toDataURL("image/png");
          } catch (e) {
            // canvas taint — leave null, original src will be used
          }
          resolve();
        };
        tempImg.onerror = resolve;
        // No crossOrigin header needed when src is a local bundled asset
        tempImg.src = sbeeLogo;
      });

      // ── 2. Clone and position off-screen ──
      clone = input.cloneNode(true);
      clone.style.position   = "absolute";
      clone.style.top        = "-9999px";
      clone.style.left       = "0";
      clone.style.width      = "1050px";
      clone.style.margin     = "0";
      clone.style.visibility = "visible";
      clone.style.background = "#ffffff";
      document.body.appendChild(clone);

      // ── 2b. Strip edit-only controls (Edit/Save/Cancel buttons) and
      //        convert any live <textarea> (Terms & Conditions edit mode)
      //        into plain text so the PDF always renders the saved/typed
      //        text, never an empty-looking input box ──
      clone.querySelectorAll(".no-pdf").forEach((el) => el.remove());
      clone.querySelectorAll("textarea").forEach((ta) => {
        const div = document.createElement("div");
        div.textContent = ta.value;
        div.style.cssText = "font-family:Abadi,'Segoe UI',Calibri,Arial,sans-serif; font-size:13px; white-space:pre-wrap;";
        ta.parentNode.replaceChild(div, ta);
      });

      // ── 3. Inject the base64 logo into every img in the clone ──
      if (logoBase64) {
        clone.querySelectorAll("img").forEach((img) => {
          img.src = logoBase64;
        });
      }

      // ── 4a. Wait for images to settle FIRST — the logo (and any other
      //       image) loading can reflow the clone's layout, shifting row
      //       positions. Boundaries must be measured on the settled layout,
      //       otherwise they go stale relative to what html2canvas actually
      //       captures below, and a row near a page-break threshold (e.g.
      //       row 16) ends up sliced in the wrong place — appearing cut off
      //       or duplicated across the page break. ──
      await new Promise((r) => setTimeout(r, 800));

      // ── 4b. NOW record row boundaries, so we know safe places to cut
      //       between pages — never through the middle of a <tr> or a
      //       Terms & Conditions <li> ──
      //       These come from getBoundingClientRect(), i.e. CSS px in the
      //       clone's own coordinate space (clone is rendered at 1050 CSS
      //       px wide). Keep hold of that width now — it's what we need
      //       below to convert these CSS-px boundaries into canvas px. ──
      const cloneRectAtMeasure = clone.getBoundingClientRect();
      const cloneTop      = cloneRectAtMeasure.top;
      const cloneWidthCss = cloneRectAtMeasure.width;
      const rowBoundaries = Array.from(clone.querySelectorAll("tr, li")).map((el) => {
        const r = el.getBoundingClientRect();
        return { top: r.top - cloneTop, bottom: r.bottom - cloneTop };
      });

      // ── 5. Capture at 2× scale for crisp output ──
      const canvas = await html2canvas(clone, {
        useCORS: false,       // not needed — all assets are now inline base64
        allowTaint: true,
        scale: 2,
        backgroundColor: "#ffffff",
        imageTimeout: 15000,
        logging: false
      });

      // ── 6. Build A4 PDF — fill full page width, paginate at row
      //       boundaries so a <tr> (e.g. the totals row) is never split
      //       across two pages ──
      const A4_W = 210;   // mm
      const A4_H = 297;   // mm

      const imgW       = A4_W;
      const pxPerMm     = canvas.width / A4_W;       // canvas px per mm — for mapping mm (page size) to canvas px
      const pageHeightPx = A4_H * pxPerMm;            // one A4 page worth of canvas px

      // ── FIX: row boundaries were measured in CSS px (clone's own layout,
      //       1050px wide), but were being scaled by pxPerMm (canvas-px-
      //       per-MM) instead of the actual CSS-px → canvas-px render
      //       scale. Those are different units — pxPerMm blew every
      //       boundary up ~5× too large, so the "safe cut line" search
      //       below almost never found a real row edge near the page
      //       break, and rows (e.g. row 16) got sliced right through the
      //       middle instead of pushed whole onto the next page.
      //       renderScale is the CSS-px → canvas-px factor html2canvas
      //       actually used (≈2, matching `scale: 2` above, but derived
      //       from the real widths rather than hardcoded). ──
      const renderScale = canvas.width / cloneWidthCss;
      const rowBoundariesPx = rowBoundaries.map(b => ({
        top:    b.top    * renderScale,
        bottom: b.bottom * renderScale
      }));

      const pdf = new jsPDF("p", "mm", "a4");

      let cursorPx  = 0;
      let firstPage = true;
      const totalPx = canvas.height;

      while (cursorPx < totalPx - 1) {
        const idealEnd = cursorPx + pageHeightPx;

        // Find the lowest row-bottom that is <= idealEnd and > cursorPx.
        // That's our safe cut line for this page.
        let cutPx = idealEnd;
        if (idealEnd < totalPx) {
          let bestBoundary = null;
          for (const b of rowBoundariesPx) {
            if (b.bottom > cursorPx && b.bottom <= idealEnd) {
              if (!bestBoundary || b.bottom > bestBoundary) bestBoundary = b.bottom;
            }
          }
          // Only use the boundary if it actually makes progress; otherwise
          // (a single row taller than one page) fall back to a hard cut.
          if (bestBoundary && bestBoundary > cursorPx + 1) {
            cutPx = bestBoundary;
          }
        } else {
          cutPx = totalPx;
        }

        const sliceHeightPx = Math.min(cutPx, totalPx) - cursorPx;

        // Crop this page's slice onto its own canvas
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width  = canvas.width;
        pageCanvas.height = sliceHeightPx;
        pageCanvas.getContext("2d").drawImage(
          canvas,
          0, cursorPx, canvas.width, sliceHeightPx,
          0, 0, canvas.width, sliceHeightPx
        );

        const sliceHeightMm = sliceHeightPx / pxPerMm;

        if (!firstPage) pdf.addPage();
        pdf.addImage(
          pageCanvas.toDataURL("image/png"),
          "PNG",
          0, 0,
          imgW,
          sliceHeightMm
        );

        cursorPx += sliceHeightPx;
        firstPage = false;
      }

      pdf.save(rfqId + "_Quotation.pdf");

      if (searchParams.get("download") === "true") {
        setTimeout(() => window.close(), 1500);
      }

    } catch (err) {
      console.error("PDF Error:", err);
      alert("PDF generation failed. Please try again.");
    } finally {
      if (clone && clone.parentNode) {
        clone.parentNode.removeChild(clone);
      }
    }
  };

  useEffect(() => {
    loadRFQ();
  }, [rfqId]);

  // ── Company address (letterhead) now comes from Configuration > Location
  //    Master instead of being hardcoded. Phone/fax/email/website/CIN stay
  //    hardcoded as-is. Falls back to the original static text if the
  //    location master hasn't been set up yet, so nothing breaks.
  //
  //    Multiple addresses are told apart using the "Label" field on each
  //    Location Master record — tag one entry "Unit 1" (Regd. Office) and
  //    another "Unit 2" (manufacturing) and they'll land in the right spot
  //    below. If no record is labelled "Unit 2" yet, the first saved
  //    location is used there (and in the top letterhead) so things keep
  //    working before labels are set up. ──
  useEffect(() => {
    const loadCompanyLocations = async () => {
      try {
        const res = await fetch(`${MASTER_API}/location-master`);
        const data = await res.json();
        const locations = Array.isArray(data) ? data : [];

        const byLabel = (keyword) =>
          locations.find((loc) =>
            (loc.location_label || "").toLowerCase().includes(keyword)
          );

        const unit2 = byLabel("2") || locations[0] || null;
        const unit1 = byLabel("1") || null;

        setCompanyLocation(unit2);
        setUnit1Location(unit1);
      } catch (err) {
        console.error("LOCATION MASTER FETCH ERROR", err);
        setCompanyLocation(null);
        setUnit1Location(null);
      }
    };
    loadCompanyLocations();
  }, []);

  useEffect(() => {
    if (!rfqData) return;

    if (searchParams.get("download") === "true") {
      // Wait for two animation frames (lets the browser finish layout/paint
      // of the table, including the totals row) before the extra safety
      // delay. A flat 1200ms guess could fire before a large product table
      // had finished laying out, which is what was causing the totals row
      // to be missing/blank in the generated PDF.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            downloadPDF();
          }, 600);
        });
      });
    }

    if (searchParams.get("print") === "true") {
      setTimeout(() => {
        window.print();
      }, 1200);
    }
  }, [rfqData, searchParams]);

  const loadRFQ = async () => {
    try {
      const res = await fetch(`${ORDER_API}/quotation/${rfqId}`, {
  headers: {
    ...getAuthHeaders(),
  },
});
      const data = await res.json();

      console.log("RFQ DATA:", data);

      setRfqData(data);

      const customerCode = data?.customer?.split(" - ")[0];

      console.log("CUSTOMER CODE:", customerCode);

      if (customerCode) {
        loadCustomer(customerCode, data);
      }
    } catch (err) {
      console.error("RFQ ERROR", err);
    }

    // ── General Terms & Conditions TEMPLATE — the shared master-service
    //    config (Configuration > Terms & Conditions). Any edit made there
    //    updates every quotation that hasn't been individually overridden.
    //    Template is one JSON blob: { template_key, content: { rows }, updated_at }.
    //    404 just means nobody has saved a template yet — keep the fallback.
    //    An empty rows array IS a valid saved state (all rows removed in
    //    Config) and should be honored, not treated as "fetch failed". ──
    try {
      const templateRes = await fetch(`${MASTER_API}/configuration/templates/quotation`);
      const templateJson = templateRes.ok ? await templateRes.json() : null;
      const rows = templateJson?.content?.rows;
      if (Array.isArray(rows)) {
        setTemplateTerms(rows);
      }
      // else: keep FALLBACK_TERMS_DEFAULTS already in state (no template saved yet)
    } catch (err) {
      console.error("TERMS TEMPLATE FETCH ERROR", err);
      // keep FALLBACK_TERMS_DEFAULTS already in state
    }

    // ── Saved General Terms & Conditions overrides (edited on the Terms tab
    //    in the RFQ form). Falls back to the template above for any row
    //    that hasn't been customized for THIS quotation only. ──
    try {
      const termsRes = await fetch(`${ORDER_API}/quotation/${rfqId}/terms`, {
  headers: {
    ...getAuthHeaders(),
  },
});
      const terms = termsRes.ok ? await termsRes.json() : [];
      setSavedTerms(terms || []);
    } catch (err) {
      console.error("TERMS FETCH ERROR", err);
      setSavedTerms([]);
    }

    // ── Saved letterhead (company name/address) override for THIS quotation
    //    only. Falls back to Location Master / static defaults above if
    //    nothing has been saved for this rfqId yet. ──
    try {
      const letterheadRes = await fetch(`${ORDER_API}/quotation/${rfqId}/letterhead`, {
        headers: {
          ...getAuthHeaders(),
        },
      });
      const letterhead = letterheadRes.ok ? await letterheadRes.json() : null;
      setLetterheadOverride(letterhead || null);
    } catch (err) {
      console.error("LETTERHEAD FETCH ERROR", err);
      setLetterheadOverride(null);
    }

    // ── Saved "Address and Contact details" (customer block) override for
    //    THIS quotation only. Falls back to the Business Partner record
    //    above if nothing has been saved for this rfqId yet. ──
    try {
      const customerOverrideRes = await fetch(`${ORDER_API}/quotation/${rfqId}/customer-address`, {
        headers: {
          ...getAuthHeaders(),
        },
      });
      const savedCustomer = customerOverrideRes.ok ? await customerOverrideRes.json() : null;
      setCustomerOverride(savedCustomer || null);
    } catch (err) {
      console.error("CUSTOMER ADDRESS FETCH ERROR", err);
      setCustomerOverride(null);
    }

    // ── Saved greeting-line override for THIS quotation only. Falls back to
    //    DEFAULT_GREETING_TEXT above if nothing has been saved yet. ──
    try {
      const greetingRes = await fetch(`${ORDER_API}/quotation/${rfqId}/greeting`, {
        headers: {
          ...getAuthHeaders(),
        },
      });
      const savedGreeting = greetingRes.ok ? await greetingRes.json() : null;
      setGreetingOverride(savedGreeting?.greeting_text ?? null);
    } catch (err) {
      console.error("GREETING FETCH ERROR", err);
      setGreetingOverride(null);
    }
  };

const loadCustomer = async (customerCode) => {
  try {
    // /business-partner/list is the login-only dropdown feed for other modules
    // (bank columns removed). /business-partner/all needs Business Partner
    // module permission, which Sales users don't have.
    const res = await fetch(`${MASTER_API}/business-partner/list`, {
      headers: { ...getAuthHeaders() },
    });

    if (!res.ok) {
      console.error("Business partner list fetch failed:", res.status);
      return;
    }

    const data = await res.json();
    const list = Array.isArray(data) ? data : data?.data || [];

    const customer = list.find(
      (x) => x.partner_code?.trim() === customerCode?.trim()
    );

    if (!customer) {
      console.error("Customer not found for code:", customerCode);
      return;
    }

    console.log("CUSTOMER RECORD:", customer);
    setCustomerData(customer);
  } catch (err) {
    console.error("CUSTOMER ERROR", err);
  }
};

  if (!rfqData) {
    return (
      <div style={{ fontFamily: "Calibri, sans-serif", fontSize: "20px" }}>
        Loading...
      </div>
    );
  }

  const CAL = "Calibri, 'Segoe UI', Arial, sans-serif";
  const ABADI = "Abadi, 'Segoe UI', Calibri, Arial, sans-serif";
  const COURIER = "'Courier Prime', 'Courier New', monospace";
  // Company name font — applies to "SBEE CABLES (INDIA) LIMITED" wherever it
  // appears (top letterhead + footer). "Good Times" is not a Google Font, so
  // if it isn't already installed as a system/desktop font, add an
  // @font-face pointing at the font file for it to actually render; the
  // fallbacks below keep the layout sane until then.
  const GOOD_TIMES = "'Good Times', 'Segoe UI', Calibri, Arial, sans-serif";

  const baseCellStyle = {
    border: "1px solid black",
    padding: "8px 6px",
    textAlign: "center",
    fontFamily: CAL,
    fontSize: "14px",
    color: "#000",
    wordWrap: "break-word",
    overflowWrap: "break-word"
  };

  const baseCellStyleLeft = {
    border: "1px solid black",
    padding: "8px 6px",
    textAlign: "left",
    fontFamily: CAL,
    fontSize: "14px",
    color: "#000",
    wordWrap: "break-word",
    overflowWrap: "break-word"
  };

  const thStyle = {
    border: "1px solid black",
    padding: "6px 4px",
    textAlign: "center",
    fontWeight: "bold",
    fontFamily: CAL,
    fontSize: "13px",
    color: "#000",
    backgroundColor: "#fff",
    wordWrap: "break-word",
    overflowWrap: "break-word"
  };

  return (
    <div
      id="quotation-container"
      style={{
        background: "#fff",
        padding: "40px 50px",
        width: "1050px",
        margin: "0 auto",
        fontFamily: CAL,
        fontSize: "20px",
        color: "#000",
        lineHeight: "1.5",
        boxSizing: "border-box"
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap');
        /* "Good Times" is not on Google Fonts. If you have the font file
           (e.g. from your brand kit), host it and uncomment below so the
           company name renders in it everywhere instead of the fallback. */
        /* @font-face {
          font-family: 'Good Times';
          src: url('/fonts/GoodTimes-Regular.woff2') format('woff2');
          font-weight: 400;
          font-style: normal;
        } */
        @media print {
          @page { size: A4; margin: 10mm; }
          tr { break-inside: avoid; page-break-inside: avoid; }
          thead { display: table-header-group; }
          /* Browser print (Ctrl+P / window.print()) renders the live DOM,
             not the stripped html2canvas clone used by downloadPDF(), so
             toolbar/Edit controls need their own print rule to be hidden. */
          .no-pdf { display: none !important; }
        }
      `}</style>

      {/* ── Single master Edit/Save/Cancel control for the letterhead, the
             "Address and Contact details" block, and the greeting line
             below — all three toggle into edit mode together. ── */}
      <div className="no-pdf" style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
        {!editingAll ? (
          <button
            type="button"
            onClick={handleEditAll}
            style={{
              padding: "4px 12px", fontSize: "12px", fontFamily: COURIER,
              background: "#0f172a", color: "#fff", border: "none",
              borderRadius: "4px", cursor: "pointer"
            }}
          >
            ✎ Edit
          </button>
        ) : (
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              onClick={handleCancelAll}
              disabled={savingAll}
              style={{
                padding: "4px 12px", fontSize: "12px", fontFamily: COURIER,
                background: "#fff", color: "#333", border: "1px solid #ccc",
                borderRadius: "4px", cursor: savingAll ? "not-allowed" : "pointer"
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={savingAll}
              style={{
                padding: "4px 12px", fontSize: "12px", fontFamily: COURIER,
                background: "#16a34a", color: "#fff", border: "none",
                borderRadius: "4px", cursor: savingAll ? "not-allowed" : "pointer"
              }}
            >
              {savingAll ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>

      {/* ── Header: company title + address block on the left, logo on the right ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "20px"
        }}
      >
        <div style={{ fontFamily: COURIER, fontSize: "12px", color: "#000", lineHeight: "1.6", flex: 1 }}>
          {/* Top letterhead shows the per-quotation override if one has been
              saved for THIS rfqId, else Unit 1 (Regd. Office) from
              Configuration > Location Master, else the static defaults.
              The ✎ Edit control below lets this block be corrected for just
              this one quotation without touching Location Master. */}
          {!editingLetterhead ? (
            <>
              <div style={{ fontFamily: GOOD_TIMES, fontWeight: "bold", fontSize: "12px", marginBottom: "2px" }}>
                {effectiveLetterhead.company_name}
              </div>
              <div>{effectiveLetterhead.address_line1}</div>
              {effectiveLetterhead.address_line2 && <div>{effectiveLetterhead.address_line2}</div>}
              <div>
                {`${effectiveLetterhead.city} ${effectiveLetterhead.pin_code}, ${effectiveLetterhead.state}, ${effectiveLetterhead.country}`}
              </div>
              <div style={{ marginTop: "8px" }}>TEL NO. {effectiveLetterhead.tel_no}</div>
              <div>GST NO. {effectiveLetterhead.gst_no}</div>
              <div>CIN NO. {effectiveLetterhead.cin_no}</div>
            </>
          ) : (
            <div className="no-pdf" style={{ display: "flex", flexDirection: "column", gap: "4px", maxWidth: "420px" }}>
              {[
                ["company_name", "Company Name"],
                ["address_line1", "Address Line 1"],
                ["address_line2", "Address Line 2"],
                ["city", "City"],
                ["pin_code", "PIN Code"],
                ["state", "State"],
                ["country", "Country"],
                ["tel_no", "Tel No."],
                ["gst_no", "GST No."],
                ["cin_no", "CIN No."],
              ].map(([field, label]) => (
                <input
                  key={field}
                  type="text"
                  value={letterheadDraft[field] ?? ""}
                  placeholder={label}
                  onChange={(e) =>
                    setLetterheadDraft((prev) => ({ ...prev, [field]: e.target.value }))
                  }
                  style={{
                    fontFamily: COURIER,
                    fontSize: "12px",
                    padding: "4px 6px",
                    border: "1px solid #94a3b8",
                    borderRadius: "4px",
                    boxSizing: "border-box"
                  }}
                />
              ))}
            </div>
          )}

        </div>

        <img
          src={sbeeLogo}
          alt="SBEE"
          crossOrigin="anonymous"
          style={{ width: "200px" }}
        />
      </div>

     

      {/* ── Address & Contact Details (left) + QUOTATION info box (right) ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "24px",
          marginTop: "16px",
          marginBottom: "16px"
        }}
      >
        <div style={{ width: "580px", textAlign: "left" }}>
          <p
            style={{
              fontFamily: COURIER,
              fontSize: "12px",
              margin: "0 0 14px 0",
              color: "#000"
            }}
          >
            Address and Contact details:
          </p>

          {/* Shows the per-quotation override if one has been saved for THIS
              rfqId, else the Business Partner master record. The ✎ Edit
              control lets this block be corrected for just this one
              quotation without touching the Business Partner master. */}
          {!editingCustomer ? (
            <>
              <div
                style={{
                  fontFamily: COURIER,
                  fontSize: "14px",
                  fontWeight: "bold",
                  lineHeight: "26px",
                  color: "#000"
                }}
              >
                {effectiveCustomer.business_partner_name}
              </div>

              <div
                style={{
                  fontFamily: COURIER,
                  fontSize: "12px",
                  lineHeight: "26px",
                  color: "#000",
                  marginTop: "8px"
                }}
              >
                {effectiveCustomer.shipping_line1}
              </div>

              <div
                style={{
                  fontFamily: COURIER,
                  fontSize: "12px",
                  lineHeight: "26px",
                  color: "#000"
                }}
              >
                {effectiveCustomer.shipping_line2}
              </div>

              <div
                style={{
                  fontFamily: COURIER,
                  fontSize: "12px",
                  lineHeight: "26px",
                  color: "#000"
                }}
              >
                {effectiveCustomer.shipping_city},{" "}
                {effectiveCustomer.shipping_state} -{" "}
                {effectiveCustomer.shipping_zip}
              </div>

              <div
                style={{
                  marginTop: "14px",
                  fontFamily: COURIER,
                  fontSize: "12px",
                  lineHeight: "26px",
                  color: "#000"
                }}
              >
                <div>
                  <b>Kind Attn:</b> {effectiveCustomer.contact_person_name}
                </div>

                <div>
                  <b>Ph:</b> +91-{effectiveCustomer.contact_details}
                </div>

                <div>
                  <b>Email:</b> {effectiveCustomer.email}
                </div>

                <div>
                  <b>GST No:</b> {effectiveCustomer.gstin}
                </div>
              </div>
            </>
          ) : (
            <div className="no-pdf" style={{ display: "flex", flexDirection: "column", gap: "4px", maxWidth: "480px" }}>
              {[
                ["business_partner_name", "Customer Name"],
                ["shipping_line1", "Address Line 1"],
                ["shipping_line2", "Address Line 2"],
                ["shipping_city", "City"],
                ["shipping_state", "State"],
                ["shipping_zip", "PIN Code"],
                ["contact_person_name", "Kind Attn (Contact Person)"],
                ["contact_details", "Phone (without +91-)"],
                ["email", "Email"],
                ["gstin", "GST No."],
              ].map(([field, label]) => (
                <input
                  key={field}
                  type="text"
                  value={customerDraft[field] ?? ""}
                  placeholder={label}
                  onChange={(e) =>
                    setCustomerDraft((prev) => ({ ...prev, [field]: e.target.value }))
                  }
                  style={{
                    fontFamily: COURIER,
                    fontSize: "12px",
                    padding: "4px 6px",
                    border: "1px solid #94a3b8",
                    borderRadius: "4px",
                    boxSizing: "border-box"
                  }}
                />
              ))}
            </div>
          )}

        </div>

        {/* ── QUOTATION info box ── */}
        <div
          style={{
            width: "440px",
            border: "1px solid #000",
            fontFamily: COURIER,
            fontSize: "12px",
            color: "#000",
            flexShrink: 0
          }}
        >
          <div
            style={{
              textAlign: "center",
              borderBottom: "1px solid #000",
              padding: "6px 8px",
              fontWeight: "bold",
              fontSize: "14px",
              letterSpacing: "2px"
            }}
          >
            QUOTATION
          </div>

          <div style={{ padding: "10px 14px" }}>
            <div style={{ display: "flex", marginBottom: "6px" }}>
              <div style={{ width: "180px", fontWeight: "bold" }}>Quotation NO</div>
              <div>: {rfqData?.rfq_id || "-"}</div>
            </div>

            <div style={{ display: "flex", marginBottom: "6px" }}>
              <div style={{ width: "180px", fontWeight: "bold" }}>Revision No.</div>
              <div>: {rfqData?.quotation_rev_no || "-"}</div>
            </div>

            <div style={{ display: "flex", marginBottom: "6px" }}>
              <div style={{ width: "180px", fontWeight: "bold" }}>Date</div>
              <div>
                : {rfqData?.creation_date
                  ? new Date(rfqData.creation_date).toLocaleDateString("en-GB")
                  : "-"}
              </div>
            </div>

            <div style={{ display: "flex", marginBottom: "6px" }}>
              <div style={{ width: "180px", fontWeight: "bold" }}>Customer No.</div>
              <div>: {customerData?.partner_code || "-"}</div>
            </div>

            <div style={{ display: "flex", marginBottom: "6px", whiteSpace: "nowrap" }}>
              <div style={{ width: "180px", fontWeight: "bold" }}>Quotation Generated By</div>
              <div>: {nameFromEmail(rfqData?.created_by) || "-"}</div>
            </div>

            <div style={{ display: "flex", marginBottom: "10px" }}>
              <div style={{ width: "180px", fontWeight: "bold" }}>Email Id</div>
              <div>: {createdByEmail || "-"}</div>
            </div>
          </div>
        </div>
      </div>

     

      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        {!editingGreeting ? (
          <p style={{ fontFamily: COURIER, fontSize: "15px", margin: "8px 0", color: "#000", flex: 1 }}>
            {effectiveGreeting}
          </p>
        ) : (
          <textarea
            className="no-pdf"
            value={greetingDraft}
            onChange={(e) => setGreetingDraft(e.target.value)}
            rows={2}
            style={{
              flex: 1,
              fontFamily: COURIER,
              fontSize: "15px",
              color: "#000",
              border: "1px solid #94a3b8",
              borderRadius: "4px",
              padding: "6px",
              resize: "vertical",
              boxSizing: "border-box",
              margin: "8px 0"
            }}
          />
        )}
      </div>

      {/* ── PRODUCT TABLE with alternating grey/white rows ── */}
      <table
        cellPadding="0"
        cellSpacing="0"
        width="100%"
        style={{
          borderCollapse: "collapse",
          border: "1px solid black",
          marginTop: "16px",
          tableLayout: "fixed"
        }}
      >
        {/* ── Explicit column widths, summing to exactly 100%. This is the
               source of truth for table-layout:fixed — the previous widths
               declared on <th> (which summed to 109% once you account for
               rowSpan/colSpan) caused every column to be squeezed down
               proportionally, crushing narrow ones like "Sl No"/"UOM" so
               badly that word-wrap broke their labels letter by letter. ── */}
        <colgroup>
          <col style={{ width: "6%" }} />   {/* Sl No */}
          <col style={{ width: "10%" }} />  {/* Material Code */}
          <col style={{ width: "17%" }} />  {/* Product Description */}
          <col style={{ width: "9%" }} />   {/* Qty — widened so figures like 1,000.00 / 1,00,000.00 stay on one line */}
          <col style={{ width: "6%" }} />   {/* UOM */}
          <col style={{ width: "9%" }} />   {/* Rate */}
          <col style={{ width: "17%" }} />  {/* Total — widened so Sub Total / GST breakdown / grand Total figures never crowd or overflow */}
          <col style={{ width: "15%" }} />  {/* Tolerance on Length */}
          <col style={{ width: "11%" }} />  {/* Minimum Order Quantity */}
        </colgroup>
        <thead>
          <tr>
            <th style={thStyle}>Sl No</th>
            <th style={thStyle}>Material Code</th>
            <th style={thStyle}>Description</th>
            <th style={thStyle}>Qty</th>
            <th style={thStyle}>UOM</th>
            <th style={thStyle}>Rate</th>
            <th style={thStyle}>Total</th>
            <th style={thStyle}>Tolerance on Qty</th>
            <th style={thStyle}>Minimum Order Quantity</th>
          </tr>
        </thead>

        <tbody>
          {displayedProductDetails.map((item, index) => {
            const rowBg  = index % 2 === 0 ? "#ffffff" : "#e8e8e8";
            const cell   = { ...baseCellStyle,     backgroundColor: rowBg };
            const cellL  = { ...baseCellStyleLeft, backgroundColor: rowBg };
            const cellR  = { ...baseCellStyle,     backgroundColor: rowBg, textAlign: "right" };

            const qty        = parseFloat(item.quantity   || 0);
            const unitPrice  = parseFloat(item.unit_price || 0);
            const disc       = parseFloat(item.discount   || 0);
            const gross      = unitPrice * qty;
            const discounted = item.discounted_price
              ? parseFloat(item.discounted_price)
              : gross - (disc * gross) / 100;
            const net        = item.net_price_after_discount
              ? parseFloat(item.net_price_after_discount)
              : discounted;
            const cgst       = parseFloat(item.cgst_amount || 0);
            const sgst       = parseFloat(item.sgst_amount || 0);
            const igst       = parseFloat(item.igst_amount || 0);
            const totalGst   = (parseFloat(item.total_gst || 0) || (cgst + sgst + igst));
            //eslint-disable-next-line
            const grand      = (parseFloat(item.final_price_after_gst || 0) || (net + totalGst));

            // Rate = Spl. Price: the price per unit AFTER Markup (%) and Discount (%),
            // i.e. the same figure as the Spl. Price column in the quotation grid.
            // Computed as Net ÷ Qty so Rate x Qty always equals the Total printed beside it.
            const splRate    = qty > 0 ? net / qty : unitPrice;

            const fmtN = (v) => {
              const n = Number(v);
              if (isNaN(n)) return "0.00";
              return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            };

            // ── Tolerance on Length — pulled from this line's Characteristics
            //    (Over Tolerance / Under Tolerance rows). Shown as "+X%, -Y%".
            //    If either of those characteristics has its tolerance_applied
            //    checkbox marked, the tolerance is per batch length; otherwise
            //    it's treated as applying to the ordered quantity. ──
            const chars = item.characteristics || [];
            const findChar = (name) =>
              chars.find(
                (c) =>
                  (c.characteristic || "").trim().toLowerCase() === name
              );
            const overTol  = findChar("over tolerance");
            const underTol = findChar("under tolerance");

            let toleranceValueText = "-";
            let toleranceApplyText = "";
            if (overTol || underTol) {
              const overVal  = overTol  ? parseFloat(overTol.value)  : null;
              const underVal = underTol ? parseFloat(underTol.value) : null;

              const parts = [];
              if (overVal !== null && !isNaN(overVal))  parts.push(`+${overVal}%`);
              if (underVal !== null && !isNaN(underVal)) parts.push(`-${underVal}%`);

              const isBatchLength =
                (overTol?.tolerance_applied === 1 || overTol?.tolerance_applied === true) ||
                (underTol?.tolerance_applied === 1 || underTol?.tolerance_applied === true);

              if (parts.length) {
                toleranceValueText = parts.join(", ");
                toleranceApplyText = `(Applied on ${isBatchLength ? "batch length" : "Order Qty"})`;
              } else {
                toleranceValueText = "-";
              }
            }

            return (
              <tr key={item.id}>
                <td style={cell}>{index + 1}</td>
                <td style={{ ...cell, whiteSpace: "nowrap", fontSize: "13px" }}>
                  {item.product_code}
                </td>
                <td style={cellL}>{item.description}</td>
                <td style={{ ...cell, whiteSpace: "nowrap" }}>{fmtN(qty)}</td>
                <td style={cell}>{item.uom || "-"}</td>
                <td style={cellR}>{fmtN(splRate)}</td>
                <td style={{ ...cellR, fontWeight: 700 }}>{fmtN(net)}</td>
                <td style={{ ...cell, fontSize: "11px", whiteSpace: "nowrap" }}>
                  <div>{toleranceValueText}</div>
                  {toleranceApplyText && <div>{toleranceApplyText}</div>}
                </td>
                <td style={cell}>{item.minimum_order_quantity ?? "-"}</td>
              </tr>
            );
          })}

          {/* ── TOTALS ROW — kept inside <tbody> instead of <tfoot> so
                 html2canvas reliably captures it on the cloned, off-screen
                 element used for PDF generation. <tfoot> rows have been
                 observed to get dropped/mis-laid-out by html2canvas when
                 the table is cloned and rendered at position:absolute. ── */}
          {(() => {
            const fmtN = (v) => {
              const n = Number(v);
              if (isNaN(n)) return "0.00";
              return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            };

            const totals = displayedProductDetails.reduce(
              (acc, item) => {
                const qty        = parseFloat(item.quantity   || 0);
                const unitPrice  = parseFloat(item.unit_price || 0);
                const disc       = parseFloat(item.discount   || 0);
                const gross      = unitPrice * qty;
                const discounted = item.discounted_price
                  ? parseFloat(item.discounted_price)
                  : gross - (disc * gross) / 100;
                const net        = item.net_price_after_discount
                  ? parseFloat(item.net_price_after_discount)
                  : discounted;
                const cgst       = parseFloat(item.cgst_amount || 0);
                const sgst       = parseFloat(item.sgst_amount || 0);
                const igst       = parseFloat(item.igst_amount || 0);
                const totalGst   = (parseFloat(item.total_gst || 0) || (cgst + sgst + igst));
                const grand      = (parseFloat(item.final_price_after_gst || 0) || (net + totalGst));
                return {
                  net:      acc.net      + net,
                  cgst:     acc.cgst     + cgst,
                  sgst:     acc.sgst     + sgst,
                  igst:     acc.igst     + igst,
                  totalGst: acc.totalGst + totalGst,
                  grand:    acc.grand    + grand,
                };
              },
              { net: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0, grand: 0 }
            );

            // ── Rate shown next to each label (e.g. "CGST @9%") — only when
            //    every line item with a non-zero amount for that tax shares
            //    the SAME rate. If rates differ across lines, the % is
            //    omitted for that row rather than showing a misleading
            //    single number. ──
            const uniformRate = (amountKey, rateKey) => {
              const rates = displayedProductDetails
                .filter((item) => parseFloat(item[amountKey] || 0) > 0)
                .map((item) => parseFloat(item[rateKey] || 0));
              if (rates.length === 0) return null;
              const allSame = rates.every((r) => r === rates[0]);
              return allSame ? rates[0] : null;
            };
            const cgstRate = uniformRate("cgst_amount", "cgst");
            const sgstRate = uniformRate("sgst_amount", "sgst");
            const igstRate = uniformRate("igst_amount", "igst");

            const currencyLabel = displayedProductDetails?.[0]?.currency || "INR";

            const footTd = {
              border: "1px solid black",
              padding: "8px 4px",
              fontFamily: CAL,
              fontSize: "13px",
              fontWeight: "700",
              backgroundColor: "#f1f5f9",
              color: "#000",
              textAlign: "right",
              whiteSpace: "nowrap",
            };

            const taxLabel = (name, rate) =>
              rate !== null && rate !== undefined ? `${name} @${rate}%` : name;

            return (
              <>
                {/* ── SUBTOTAL ROW — sum of all product line totals, before GST ── */}
                <tr>
                  <td colSpan={6} style={{ ...footTd, textAlign: "right", fontWeight: "600", color: "#333", fontSize: "20px" }}>
                    {currencyLabel} ₹
                  </td>
                  <td style={{ ...footTd, textAlign: "right" }}>
                    Sub Total: {fmtN(totals.net)}
                  </td>
                  <td style={footTd}></td>
                  <td style={footTd}></td>
                </tr>
                {(totals.cgst > 0 || totals.sgst > 0 || totals.igst > 0) && (
                  <tr>
                    <td colSpan={6} style={{ ...footTd, textAlign: "left", fontWeight: "600", color: "#333", verticalAlign: "top" }}>
                    </td>
                    {/* Mini two-column table so labels stay left and every
                        amount lines up in a single right-aligned column,
                        regardless of label length ("CGST @9%" vs "Total GST"). */}
                    <td style={{ ...footTd, textAlign: "left", padding: 0, whiteSpace: "normal" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                        <colgroup>
                          <col style={{ width: "55%" }} />
                          <col style={{ width: "45%" }} />
                        </colgroup>
                        <tbody>
                          {totals.cgst > 0 && (
                            <tr>
                              <td style={{ border: "none", padding: "2px 4px", textAlign: "left", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000", overflowWrap: "break-word" }}>
                                {taxLabel("CGST", cgstRate)}
                              </td>
                              <td style={{ border: "none", padding: "2px 4px", textAlign: "right", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000", overflowWrap: "break-word" }}>
                                {fmtN(totals.cgst)}
                              </td>
                            </tr>
                          )}
                          {totals.sgst > 0 && (
                            <tr>
                              <td style={{ border: "none", padding: "2px 4px", textAlign: "left", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000", overflowWrap: "break-word" }}>
                                {taxLabel("SGST", sgstRate)}
                              </td>
                              <td style={{ border: "none", padding: "2px 4px", textAlign: "right", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000", overflowWrap: "break-word" }}>
                                {fmtN(totals.sgst)}
                              </td>
                            </tr>
                          )}
                          {totals.igst > 0 && (
                            <tr>
                              <td style={{ border: "none", padding: "2px 4px", textAlign: "left", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000", overflowWrap: "break-word" }}>
                                {taxLabel("IGST", igstRate)}
                              </td>
                              <td style={{ border: "none", padding: "2px 4px", textAlign: "right", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000", overflowWrap: "break-word" }}>
                                {fmtN(totals.igst)}
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td style={{ border: "none", padding: "2px 4px", textAlign: "left", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000", overflowWrap: "break-word" }}>
                              Total GST
                            </td>
                            <td style={{ border: "none", padding: "2px 4px", textAlign: "right", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000", overflowWrap: "break-word" }}>
                              {fmtN(totals.totalGst)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                    <td style={footTd}></td>
                    <td style={footTd}></td>
                  </tr>
                )}
                <tr>
                  <td colSpan={6} style={{ ...footTd, textAlign: "left", fontWeight: "600", color: "#333" }}>
                  </td>
                  <td style={{ ...footTd, textAlign: "right", fontSize: "14px", color: "#0f172a", fontWeight: "800" }}>
                    TOTAL: {fmtN(totals.grand)}
                  </td>
                  <td style={footTd}></td>
                  <td style={footTd}></td>
                </tr>
              </>
            );
          })()}
        </tbody>
      </table>

      <br />

      {/* ── General Terms & Conditions — fixed table per the corrected quotation
             format. Payment Terms is pulled from the dropdown selection made on
             the RFQ's Basic tab (Home.js); everything else is standard text. ── */}
      <div style={{ marginTop: "20px" }}>
        <div
          className="no-pdf"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            margin: "0 0 8px 0"
          }}
        >
          <p style={{
            fontFamily: CAL, fontSize: "20px", fontWeight: "normal",
            textDecoration: "underline", margin: 0, color: "#000"
          }}>
            General Terms &amp; Conditions
          </p>

          {!editingTerms ? (
            <button
              type="button"
              onClick={handleEditTerms}
              style={{
                padding: "6px 14px", fontSize: "13px", fontFamily: CAL,
                background: "#0f172a", color: "#fff", border: "none",
                borderRadius: "4px", cursor: "pointer"
              }}
            >
              ✎ Edit
            </button>
          ) : (
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                onClick={handleCancelTerms}
                disabled={savingTerms}
                style={{
                  padding: "6px 14px", fontSize: "13px", fontFamily: CAL,
                  background: "#fff", color: "#333", border: "1px solid #ccc",
                  borderRadius: "4px", cursor: savingTerms ? "not-allowed" : "pointer"
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveTerms}
                disabled={savingTerms}
                style={{
                  padding: "6px 14px", fontSize: "13px", fontFamily: CAL,
                  background: "#16a34a", color: "#fff", border: "none",
                  borderRadius: "4px", cursor: savingTerms ? "not-allowed" : "pointer"
                }}
              >
                {savingTerms ? "Saving…" : "Save"}
              </button>
            </div>
          )}
        </div>

        {/* Underline for the heading is on the <p> above; this line just
            keeps the same underline decoration when the toolbar row is
            visible above the table. */}

        <table
          cellPadding="0"
          cellSpacing="0"
          width="100%"
          style={{
            borderCollapse: "collapse",
            border: "1px solid black",
            tableLayout: "fixed"
          }}
        >
          <colgroup>
            <col style={{ width: "5%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "75%" }} />
          </colgroup>
          <tbody>
            {templateTerms.map((row, i) => (
              <tr key={row.term_no}>
                <td style={{ ...baseCellStyle, fontFamily: ABADI, textAlign: "center", verticalAlign: "top" }}>
                  {i + 1}.
                </td>
                <td style={{ ...baseCellStyleLeft, fontFamily: ABADI, fontWeight: "bold", verticalAlign: "top" }}>
                  {row.title}
                </td>
                <td style={{ ...baseCellStyleLeft, fontFamily: ABADI, verticalAlign: "top" }}>
                  {editingTerms ? (
                    <textarea
                      value={termsDraft[row.term_no] ?? ""}
                      onChange={(e) =>
                        setTermsDraft((prev) => ({ ...prev, [row.term_no]: e.target.value }))
                      }
                      rows={Math.max(2, Math.ceil((termsDraft[row.term_no]?.length || 0) / 90))}
                      style={{
                        width: "100%",
                        fontFamily: ABADI,
                        fontSize: "13px",
                        color: "#000",
                        border: "1px solid #94a3b8",
                        borderRadius: "4px",
                        padding: "6px",
                        resize: "vertical",
                        boxSizing: "border-box"
                      }}
                    />
                  ) : (
                    getEffectiveTermText(row.term_no)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

     

      <br />

      <p
        style={{
          fontFamily: CAL,
          fontSize: "20px",
          fontWeight: "400",
          margin: "4px 0",
          color: "#000"
        }}
      >
        Thanks &amp; Regards,
      </p>

      <p
        style={{
          fontFamily: CAL,
          fontSize: "20px",
          fontWeight: "bold",
          margin: "10px 0 4px 0",
          color: "#000"
        }}
      >
        FOR SBEE Cables (India) Limited, Bangalore
      </p>

      <div style={{ pageBreakInside: "avoid", breakInside: "avoid", marginTop: "10px" }}>

        <p style={{ fontFamily: CAL, fontSize: "19px", margin: "3px 0", color: "#000" }}>
          {customerData?.business_partner_responsible || customerData?.sales_person || "Sales Team"}
        </p>
        <p style={{ fontFamily: CAL, fontSize: "19px", margin: "3px 0", color: "#000" }}>
          Ph: +91 {customerData?.partner_responsible_phone || "-"}
        </p>
        <p style={{ fontFamily: CAL, fontSize: "19px", margin: "3px 0", color: "#000" }}>
          Email: {customerData?.partner_responsible_email || "-"}
        </p>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "20px",
            fontFamily: COURIER,
            fontSize: "13px",
            color: "#000"
          }}
        >
          <div>
            <b>Prepared by :</b> {nameFromEmail(rfqData?.created_by)}
          </div>

          <div>
            <b>Approved by :</b> {nameFromEmail(rfqData?.internal_action_by)}
            {approverSealUrl && (
              <div style={{ marginTop: "6px" }}>
                <img
                  src={approverSealUrl}
                  alt="Seal & Sign"
                  crossOrigin="anonymous"
                  style={{ maxHeight: "70px", maxWidth: "160px", objectFit: "contain" }}
                />
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            marginTop: "60px",
            marginLeft: "-50px",
            marginRight: "-50px",
            paddingLeft: "50px",
            paddingRight: "50px",
            borderTop: "1px solid #000",
            paddingTop: "12px",
            textAlign: "center",
            fontFamily: "Arial, Helvetica, sans-serif",
            color: "#444"
          }}
        >
          <div
            style={{
              fontFamily: GOOD_TIMES,
              fontSize: "30px",
              fontWeight: "700",
              color: "#d62b43",
              letterSpacing: "1px",
              lineHeight: "1.2"
            }}
          >
            {companyDisplayName}
          </div>

          {/* Footer always lists BOTH units, from Configuration > Location
              Master. Fallback text below only applies if Location Master
              hasn't been set up yet, and matches each unit's real address
              (previously these two fallbacks were swapped). */}
          <div style={{ fontSize: "15px", marginTop: "5px", lineHeight: "1.5" }}>
            Regd. Off. &amp; Unit -1 : {unit1Location
              ? `${unit1Location.address_line1}${unit1Location.address_line2 ? ", " + unit1Location.address_line2 : ""}, ${unit1Location.city} - ${unit1Location.pin_code}, ${unit1Location.state}, ${unit1Location.country?.toUpperCase()}`
              : "No.282 & 283, KIADB Industrial Area, Phase 2, Harohalli, Kanakapura Taluk, Ramanagar - 562112, Karnataka, INDIA"}
          </div>

          <div style={{ fontSize: "15px", lineHeight: "1.5" }}>
            Unit -2 : {companyLocation
              ? `${companyLocation.address_line1}${companyLocation.address_line2 ? ", " + companyLocation.address_line2 : ""}, ${companyLocation.city} - ${companyLocation.pin_code}, ${companyLocation.state}, ${companyLocation.country?.toUpperCase()}`
              : "# 29, JC Industrial Estate, Kanakapura Road, Bangalore - 560062, INDIA"}
          </div>

          {/* Phone/fax/email/CIN also sourced from Unit 1 (Regd. Office) in
              Configuration > Location Master. Location Master has no
              "website" field, so that one stays static. Falls back to the
              old static values only if Location Master / these particular
              fields haven't been filled in yet. */}
          <div style={{ fontSize: "15px", marginTop: "4px", lineHeight: "1.5" }}>
            ☎ {[unit1Location?.phone1, unit1Location?.phone2]
              .filter(Boolean)
              .map((p) => (p.trim().startsWith("+") ? p.trim() : `+${p.trim()}`))
              .join(", ") || "+91 80 2666 2172, +91 80 26860166"} &nbsp;&nbsp;
            📠 {unit1Location?.phone3
              ? (unit1Location.phone3.trim().startsWith("+") ? unit1Location.phone3.trim() : `+${unit1Location.phone3.trim()}`)
              : "+91 80 2666 7339"} &nbsp;&nbsp;
            ✉ {unit1Location?.email1 || "info@sbeecables.in"} &nbsp;&nbsp;
            🌐 www.sbeecables.in &nbsp;&nbsp;
            CIN: {unit1Location?.cin_no || "U64203KA1978PLC003385"}
          </div>
        </div>

      </div>

    </div>
  );
}

export default QuotationView;